import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getConsentRecords,
  getDashboard,
  setDigestPreference,
  logoutAll,
} from '../../services/api';

const C = {
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#F8FAFC',
  text: '#1E293B',
  textMuted: '#64748B',
  textSubtle: '#94A3B8',
  accent: '#0066FF',
  accentDark: '#0052CC',
  accentSoft: '#EFF4FF',
  border: '#E2E8F0',
  red: '#EF4444',
  redSoft: '#FEF2F2',
  redBorder: '#FECACA',
  redText: '#B91C1C',
  green: '#059669',
  greenSoft: '#ECFDF5',
  greenBorder: '#A7F3D0',
};

const HEADING_FONT = "'Syne', sans-serif";
const BODY_FONT    = "'DM Sans', sans-serif";

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Show only the last 8 chars of the IP hash on screen — enough for an
// auditor to spot duplicates between consents without exposing anything
// useful for re-identification.
function ipHashTail(hash) {
  if (!hash) return '—';
  return `…${String(hash).slice(-8)}`;
}

const CONSENT_LABELS = {
  TERMS:                 'Terms of Service',
  CHILD_DATA_PROCESSING: 'Parental responsibility for data processing',
  ACCOUNT_CREATION:      'Safeguarding system understanding',
};

export default function DataControls() {
  const navigate = useNavigate();

  const [consents, setConsents]           = useState(null);
  const [consentsErr, setConsentsErr]     = useState('');
  const [digestEnabled, setDigestEnabled] = useState(null);
  const [digestSaving, setDigestSaving]   = useState(false);
  const [digestErr, setDigestErr]         = useState('');
  const [showSignOutAll, setShowSignOutAll] = useState(false);
  const [signingOut, setSigningOut]       = useState(false);

  // Dashboard payload already includes weeklyDigestEnabled at the parent
  // level — we don't need a dedicated endpoint.
  useEffect(() => {
    let cancelled = false;
    getConsentRecords()
      .then((res) => { if (!cancelled) setConsents(res.data?.consents || []); })
      .catch(() => { if (!cancelled) setConsentsErr('Could not load your consent records.'); });
    getDashboard()
      .then((res) => { if (!cancelled) setDigestEnabled(!!res.data?.weeklyDigestEnabled); })
      .catch(() => { /* digest toggle just stays loading; non-critical */ });
    return () => { cancelled = true; };
  }, []);

  async function handleDigestChange(next) {
    setDigestSaving(true);
    setDigestErr('');
    try {
      await setDigestPreference(next);
      setDigestEnabled(next);
    } catch {
      setDigestErr('Could not save. Please try again.');
    }
    setDigestSaving(false);
  }

  async function handleSignOutAll() {
    setSigningOut(true);
    try { await logoutAll(); } catch { /* ignore — we're signing out anyway */ }
    try { localStorage.removeItem('accessToken'); } catch { /* ignore */ }
    navigate('/parent/login', {
      replace: true,
      state: { error: 'You\'ve been signed out on every device. Sign in again to continue.' },
    });
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: C.surfaceMuted,
      padding: '32px 16px 48px',
      fontFamily: BODY_FONT,
      color: C.text,
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 16 }}>
          <Link to="/parent/dashboard" style={{ color: C.accent, textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
            ← Back to dashboard
          </Link>
        </div>

        <h1 style={{
          margin: '0 0 6px',
          fontFamily: HEADING_FONT,
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: '-0.01em',
        }}>Your data &amp; account</h1>
        <p style={{ margin: '0 0 28px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
          Manage your consents, notifications, and signed-in devices.
        </p>

        {/* ─── Your consents ──────────────────────────────────────────────── */}
        <Card>
          <SectionTitle>Your consents</SectionTitle>
          <p style={mutedNote}>
            These records are retained for 7 years as required by law.
          </p>
          {consentsErr && <ErrorBanner>{consentsErr}</ErrorBanner>}
          {consents === null && !consentsErr && <Loading>Loading consent records…</Loading>}
          {consents && consents.length === 0 && (
            <Empty>No consent records on file yet.</Empty>
          )}
          {consents && consents.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr>
                    <Th>Consent</Th>
                    <Th>Version</Th>
                    <Th>Date agreed</Th>
                    <Th>IP (last 8)</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {consents.map((c) => (
                    <tr key={c.id}>
                      <Td>{CONSENT_LABELS[c.consent_type] || c.consent_type}</Td>
                      <Td><code style={mono}>{c.consent_text_version}</code></Td>
                      <Td>{fmtDate(c.given_at)}</Td>
                      <Td><code style={mono}>{ipHashTail(c.ip_hash)}</code></Td>
                      <Td>
                        {c.consent_given === 1
                          ? <Badge tone="green">Active</Badge>
                          : <Badge tone="grey">Withdrawn</Badge>}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ─── Notifications ──────────────────────────────────────────────── */}
        <Card>
          <SectionTitle>Notifications</SectionTitle>
          <ToggleRow
            label="Weekly session summary email"
            description="A short Monday-morning email with each child's progress for the previous week."
            checked={digestEnabled === true}
            onChange={handleDigestChange}
            disabled={digestEnabled === null || digestSaving}
            saving={digestSaving}
          />
          {digestErr && <ErrorBanner>{digestErr}</ErrorBanner>}
        </Card>

        {/* ─── Security ──────────────────────────────────────────────────── */}
        <Card>
          <SectionTitle>Security</SectionTitle>
          <p style={mutedNote}>
            If you suspect someone else has access to your account, sign out everywhere and then reset your password.
          </p>
          <button
            type="button"
            onClick={() => setShowSignOutAll(true)}
            style={{
              padding: '10px 16px',
              background: '#fff',
              color: C.redText,
              border: `1.5px solid ${C.redBorder}`,
              borderRadius: 10,
              fontFamily: HEADING_FONT,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >Sign out all devices</button>
        </Card>
      </div>

      {showSignOutAll && (
        <Modal onClose={() => !signingOut && setShowSignOutAll(false)}>
          <h2 style={{
            margin: '0 0 8px', fontFamily: HEADING_FONT, fontSize: 20, fontWeight: 700, color: C.text,
          }}>Sign out all devices?</h2>
          <p style={{ margin: '0 0 16px', color: C.textMuted, fontSize: 15, lineHeight: 1.55 }}>
            This will sign you out on all devices including this one. You'll need to sign in again with your email and password.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowSignOutAll(false)}
              disabled={signingOut}
              style={modalCancelStyle}
            >Cancel</button>
            <button
              type="button"
              onClick={handleSignOutAll}
              disabled={signingOut}
              style={{ ...modalConfirmStyle, background: C.redText }}
            >{signingOut ? 'Signing out…' : 'Sign out everywhere'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Tiny presentational helpers ──────────────────────────────────────────────

function Card({ children }) {
  return (
    <section style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: '22px 22px 20px',
      marginBottom: 16,
    }}>{children}</section>
  );
}

function SectionTitle({ children }) {
  return <h2 style={{
    margin: '0 0 8px', fontFamily: HEADING_FONT, fontSize: 17, fontWeight: 700, color: C.text,
  }}>{children}</h2>;
}

function Th({ children }) {
  return <th style={{
    padding: '8px 10px',
    textAlign: 'left',
    borderBottom: `1px solid ${C.border}`,
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: C.textMuted,
  }}>{children}</th>;
}

function Td({ children }) {
  return <td style={{
    padding: '10px',
    borderBottom: `1px solid ${C.border}`,
    fontSize: 14,
    color: C.text,
    verticalAlign: 'middle',
  }}>{children}</td>;
}

function Badge({ children, tone }) {
  const palette = tone === 'green'
    ? { bg: C.greenSoft, fg: '#065F46', bd: C.greenBorder }
    : { bg: '#F1F5F9', fg: C.textMuted, bd: C.border };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 999,
      background: palette.bg,
      color: palette.fg,
      border: `1px solid ${palette.bd}`,
      fontSize: 12,
      fontWeight: 600,
    }}>{children}</span>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled, saving }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      paddingTop: 6,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{label}</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2 }}>{description}</div>
      </div>
      <label style={{
        position: 'relative',
        display: 'inline-block',
        width: 44,
        height: 24,
        cursor: disabled ? 'wait' : 'pointer',
        flexShrink: 0,
      }}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span style={{
          position: 'absolute',
          inset: 0,
          background: checked ? C.accent : '#CBD5E1',
          borderRadius: 999,
          transition: 'background 160ms ease',
        }} />
        <span style={{
          position: 'absolute',
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          background: '#fff',
          borderRadius: '50%',
          transition: 'left 160ms ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
      </label>
      {saving && <span style={{ fontSize: 12, color: C.textMuted, width: 60 }}>Saving…</span>}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          padding: '22px 22px 20px',
        }}
      >{children}</div>
    </div>
  );
}

function ErrorBanner({ children }) {
  return (
    <div role="alert" style={{
      marginTop: 10,
      padding: '10px 14px',
      background: C.redSoft,
      border: `1px solid ${C.redBorder}`,
      borderRadius: 10,
      color: C.redText,
      fontSize: 14,
    }}>{children}</div>
  );
}

function Loading({ children }) {
  return <div style={{ padding: '14px 0', fontSize: 14, color: C.textMuted }}>{children}</div>;
}

function Empty({ children }) {
  return <div style={{ padding: '14px 0', fontSize: 14, color: C.textMuted }}>{children}</div>;
}

const mutedNote = { margin: '0 0 14px', fontSize: 13, color: C.textMuted, lineHeight: 1.55 };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 };

const modalCancelStyle = {
  padding: '10px 16px',
  background: '#fff',
  color: C.text,
  border: `1.5px solid ${C.border}`,
  borderRadius: 10,
  fontFamily: HEADING_FONT,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};

const modalConfirmStyle = {
  padding: '10px 16px',
  background: C.accent,
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  fontFamily: HEADING_FONT,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
};
